import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { ordersService } from '../services/ordersService';
import { generatePrintContent } from '../utils/printUtils';

/**
 * Hook personnalisé pour gérer la logique des commandes
 */
export const useOrders = () => {
  const { isAuthenticated, user } = useAuth();
  const { showNotification } = useNotification();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [search, setSearch] = useState('');

  const isAdminUser = user && (user.isAdmin === true || user.is_admin === true || user.isAdmin === 1 || user.is_admin === 1);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const limit = 50;
      let response;
      if (isAdminUser) {
        response = await ordersService.getAllOrders(page, limit, { search });
      } else {
        response = await ordersService.getUserOrders(page, limit, search);
      }

      const data = response.data || {};
      setOrders(data.orders || []);
      const totalFromBackend = data.totalPages || (data.total ? Math.ceil(data.total / limit) : 1);
      setTotalPages(Math.max(totalFromBackend, 1));
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      setError('Erreur lors du chargement des commandes');
      showNotification('Erreur lors du chargement des commandes', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAdminUser, page, search, showNotification]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated, fetchOrders]);

  useEffect(() => {
    if (search !== undefined) {
      fetchOrders();
    }
  }, [search, fetchOrders]);

  const handleViewDetails = async (orderId) => {
    try {
      console.log('[FRONTEND DEBUG] Chargement des détails pour la commande ID:', orderId);
      const response = await ordersService.getOrderById(orderId);
      console.log('[FRONTEND DEBUG] Réponse reçue:', response.data);

      // Le backend retourne { order: {...} }, donc on prend response.data.order
      const orderData = response.data.order || response.data;
      console.log('[FRONTEND DEBUG] Données de commande extraites:', orderData);

      setSelectedOrder(orderData);
      console.log('[FRONTEND DEBUG] selectedOrder mis à jour:', orderData);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      showNotification('Erreur lors du chargement des détails de la commande', 'error');
    }
  };

  const handleOrderSelect = (orderId, event) => {
    console.log('handleOrderSelect called for order:', orderId, 'target:', event.target);

    // Vérifier si le clic est sur la case à cocher ou ses enfants
    if (event.target.closest('.MuiCheckbox-root') || event.target.closest('[role="checkbox"]')) {
      console.log('Click on checkbox detected, ignoring');
      // Ne rien faire - la case à cocher gère sa propre sélection
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      console.log('Ctrl+click detected');
      // Ctrl+clic sur la ligne - sélection multiple
      setSelectedOrders(prev => {
        const newSet = new Set(prev);
        if (newSet.has(orderId)) {
          newSet.delete(orderId);
        } else {
          newSet.add(orderId);
        }
        return newSet;
      });
    } else {
      console.log('Normal click, opening details');
      // Clic normal sur la ligne - ouvrir les détails
      handleViewDetails(orderId);
    }
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(order => order.id)));
    }
  };

  const handleToggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleBulkCancel = async () => {
    if (selectedOrders.size === 0) return;

    try {
      const promises = Array.from(selectedOrders).map(orderId =>
        ordersService.cancelOrder(orderId)
      );
      await Promise.all(promises);

      showNotification(`${selectedOrders.size} commande(s) annulée(s) avec succès`, 'success');
      setSelectedOrders(new Set());
      fetchOrders(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de l\'annulation des commandes:', error);
      showNotification('Erreur lors de l\'annulation des commandes', 'error');
    }
  };

  const handlePrint = async (orderId = null) => {
    try {
      let ordersToPrint = [];

      if (orderId) {
        // Imprimer une commande spécifique
        const response = await ordersService.getOrderById(orderId);
        ordersToPrint = [response.data.order];
      } else if (selectedOrders.size > 0) {
        // Imprimer les commandes sélectionnées
        const promises = Array.from(selectedOrders).map(id =>
          ordersService.getOrderById(id)
        );
        const responses = await Promise.all(promises);
        ordersToPrint = responses.map(response => response.data.order);
      } else {
        showNotification('Aucune commande sélectionnée pour l\'impression', 'warning');
        return;
      }

      // Créer le contenu HTML pour l'impression
      const printContent = generatePrintContent(ordersToPrint);

      // Ouvrir une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Attendre que le contenu soit chargé puis imprimer
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };

    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      showNotification('Erreur lors de l\'impression des commandes', 'error');
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      showNotification('Génération de la facture en cours...', 'info');

      // Télécharger le PDF depuis le backend
      const pdfBlob = await ordersService.downloadInvoice(orderId);

      // Créer et télécharger le fichier PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      // Récupérer le nom du fichier depuis les headers si possible, sinon utiliser un nom par défaut
      const order = orders.find(o => o.id === orderId);
      const fileName = order
        ? `Facture_${order.orderNumber}.pdf`
        : `Facture_${orderId}_${new Date().toISOString().split('T')[0]}.pdf`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification('Facture téléchargée avec succès', 'success');

    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors du téléchargement de la facture';
      showNotification(errorMessage, 'error');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await ordersService.cancelOrder(orderId);
      showNotification('Commande annulée avec succès', 'success');
      fetchOrders(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de l\'annulation de la commande:', error);
      showNotification('Erreur lors de l\'annulation de la commande', 'error');
    }
  };

  const handleGenerateLabel = async (orderId) => {
    try {
      const order = orders.find(o => o.id === orderId);

      // Si l'étiquette existe déjà, la télécharger
      if (order?.trackingNumber) {
        showNotification('Téléchargement de l\'étiquette...', 'info');
        const response = await ordersService.downloadLabel(orderId);

        // Créer un blob et télécharger le PDF
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `etiquette_${order.orderNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showNotification('Étiquette téléchargée avec succès', 'success');
      } else {
        // Sinon, générer une nouvelle étiquette
        showNotification('Génération de l\'étiquette en cours...', 'info');
        const result = await ordersService.generateLabel(orderId);

        if (result.success) {
          showNotification(`Étiquette générée ! N° de suivi: ${result.data.trackingNumber}`, 'success');
          fetchOrders(); // Recharger pour afficher le numéro de suivi

          // Télécharger automatiquement l'étiquette
          if (result.data.labelUrl) {
            setTimeout(() => handleGenerateLabel(orderId), 500);
          }
        } else {
          showNotification(result.message || 'Erreur lors de la génération', 'error');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la génération de l\'étiquette:', error);
      showNotification('Erreur lors de la génération de l\'étiquette', 'error');
    }
  };

  const handleValidateOrder = async (orderId) => {
    try {
      await ordersService.updateOrderStatus(orderId, 'confirmed');
      showNotification('Commande validée avec succès', 'success');

      // Recharger la commande pour mettre à jour les détails
      if (selectedOrder && selectedOrder.id === orderId) {
        const response = await ordersService.getOrderById(orderId);
        setSelectedOrder(response.data.order);
      }

      fetchOrders(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de la validation de la commande:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la validation de la commande';
      showNotification(errorMessage, 'error');
    }
  };

  const handleMarkAsShipped = async (orderId) => {
    try {
      await ordersService.updateOrderStatus(orderId, 'shipped');
      showNotification('Commande marquée comme envoyée', 'success');

      // Recharger la commande pour mettre à jour les détails
      if (selectedOrder && selectedOrder.id === orderId) {
        const response = await ordersService.getOrderById(orderId);
        setSelectedOrder(response.data.order);
      }

      fetchOrders(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors du changement de statut';
      showNotification(errorMessage, 'error');
    }
  };

  const handleBulkStatusChange = async (orderIds, newStatus) => {
    if (!orderIds || (Array.isArray(orderIds) && orderIds.length === 0) || (orderIds instanceof Set && orderIds.size === 0)) return;

    const ids = Array.isArray(orderIds) ? orderIds : Array.from(orderIds);

    try {
      // Parallel updates
      const promises = ids.map(id => ordersService.updateOrderStatus(id, newStatus));
      await Promise.all(promises);

      showNotification(`${ids.length} commande(s) mise(s) à jour vers "${newStatus}"`, 'success');
      fetchOrders();
      setSelectedOrders(new Set()); // Deselect
    } catch (error) {
      console.error('Erreur lors de la mise à jour massive:', error);
      showNotification('Erreur lors de la mise à jour massive des statuts', 'error');
    }
  };

  return {
    // État
    orders,
    loading,
    selectedOrder,
    detailDialogOpen,
    page,
    totalPages,
    error,
    selectedOrders,
    search,

    // Actions
    setPage,
    setDetailDialogOpen,
    fetchOrders,
    setSearch,
    handleViewDetails,
    handleOrderSelect,
    handleSelectAll,
    handleToggleOrderSelection,
    handleBulkCancel,
    handlePrint,
    handleDownloadInvoice,
    handleCancelOrder,
    handleGenerateLabel,
    handleValidateOrder,
    handleMarkAsShipped,
    handleBulkStatusChange,
  };
};
